import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import QRCodeStyling from "qr-code-styling";
import api from "../../services/api";
import "./AnniversaireInvitation.css";

interface ThemeData {
  background: string;
  text: string;
  accent: string;
  button: string;
}

interface Anniversaire {
  _id: string;
  personne: string;
  age: number;
  province: string;
  dateISO: string;
  heureISO: string;
  lieu: string;
  image: string;
  theme: ThemeData;
  rsvp: boolean;
}

interface Invite {
  _id: string;
  nom: string;
  uniqueToken: string;
  statut: string;
}

interface InvitationResponse {
  success: boolean;
  anniversaire: Anniversaire;
  invite: Invite;
}

interface ConfirmResponse {
  success: boolean;
  message: string;
  invite: Invite;
}

const API_URL = process.env.REACT_APP_API_URL;

const AnniversaireInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [showPreloader, setShowPreloader] = useState(true);
  const [anniversaire, setAnniversaire] = useState<Anniversaire | null>(null);
  const [confirmationStatus, setConfirmationStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const [qrInitialized, setQrInitialized] = useState(false);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await api.get<InvitationResponse>(`/anniversaires/invitation/${token}`);
        if (response.data.success) {
          setAnniversaire(response.data.anniversaire);
          setConfirmationStatus(response.data.invite.statut);
        }
      } catch (err) {
        setError("Invitation introuvable ou expirée");
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  useEffect(() => {
    if (loading || showPreloader || qrInitialized || !token || !anniversaire) {
      return;
    }
    
    const timer = setTimeout(() => {
      if (qrRef.current) {
        try {
          qrCode.current = new QRCodeStyling({
            width: 200,
            height: 200,
            data: token,
            margin: 4,
            qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
            dotsOptions: { color: anniversaire.theme.button, type: "rounded" },
            backgroundOptions: { color: "#ffffff" },
            cornersSquareOptions: { color: anniversaire.theme.button, type: "extra-rounded" },
            cornersDotOptions: { color: anniversaire.theme.button, type: "dot" },
          });
          
          qrRef.current.innerHTML = "";
          qrCode.current.append(qrRef.current);
          setQrInitialized(true);
        } catch (err) {
          console.error("Erreur initialisation QR code:", err);
        }
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [loading, showPreloader, qrInitialized, token, anniversaire]);

  useEffect(() => {
    if (anniversaire) {
      const oldMeta = document.querySelectorAll('meta[property^="og:"]');
      oldMeta.forEach(meta => meta.remove());
      
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.setAttribute('content', `Anniversaire de ${anniversaire.personne} - ${anniversaire.age} ans`);
      document.head.appendChild(meta);
      
      const meta2 = document.createElement('meta');
      meta2.setAttribute('property', 'og:description');
      meta2.setAttribute('content', `Nous fêtons les ${anniversaire.age} ans de ${anniversaire.personne}. Rejoignez-nous pour célébrer ce moment special`);
      document.head.appendChild(meta2);
      
      const meta3 = document.createElement('meta');
      meta3.setAttribute('property', 'og:image');
      meta3.setAttribute('content', `${API_URL}/uploads/anniversaires/${anniversaire.image}`);
      document.head.appendChild(meta3);
      
      const meta4 = document.createElement('meta');
      meta4.setAttribute('property', 'og:url');
      meta4.setAttribute('content', window.location.href);
      document.head.appendChild(meta4);
      
      const meta5 = document.createElement('meta');
      meta5.setAttribute('property', 'og:type');
      meta5.setAttribute('content', 'website');
      document.head.appendChild(meta5);
      
      return () => {
        const metas = document.querySelectorAll('meta[property^="og:"]');
        metas.forEach(meta => meta.remove());
      };
    }
  }, [anniversaire]);

  const handleResponse = async () => {
    if (isSubmitting) return;
    
    let newStatut = "";
    if (confirmationStatus === "confirmed") {
      newStatut = "declined";
    } else if (confirmationStatus === "declined") {
      newStatut = "confirmed";
    } else {
      newStatut = "confirmed";
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.put<ConfirmResponse>(`/anniversaires/invitation/${token}`, { statut: newStatut });
      if (response.data.success) {
        setConfirmationStatus(newStatut);
      }
    } catch (err) {
      alert("Erreur lors de l'operation. Veuillez reessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreloaderFinish = () => {
    setShowPreloader(false);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };

  useEffect(() => {
    if (!showPreloader) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    interface Gift {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      rotation: number;
      rotationSpeed: number;
    }

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      alpha: number;
      gravity: number;
      life: number;
    }

    let gifts: Gift[] = [];
    let particles: Particle[] = [];
    let lastTime = Date.now();

    const createGift = () => {
      gifts.push({
        x: Math.random() * canvas.width,
        y: -30,
        vx: (Math.random() - 0.5) * 1.5,
        vy: 2 + Math.random() * 3,
        size: 25 + Math.random() * 25,
        color: `hsl(${Math.random() * 60 + 340}, 70%, 55%)`,
        rotation: 0,
        rotationSpeed: (Math.random() - 0.5) * 0.1
      });
    };

    const createExplosion = (x: number, y: number, color: string) => {
      const particleCount = 40 + Math.random() * 30;
      for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 6;
        particles.push({
          x: x,
          y: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 4,
          color: color,
          alpha: 1,
          gravity: 0.15,
          life: 1
        });
      }
    };

    const launchFirework = () => {
      const x = 50 + Math.random() * (canvas.width - 100);
      const y = canvas.height * 0.2 + Math.random() * (canvas.height * 0.4);
      const colors = ['#ff4d4d', '#ff9f4d', '#ff4d9f', '#4dff4d', '#4d9fff', '#ff4dff', '#ffd700', '#ff6b6b'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      createExplosion(x, y, color);
    };

    for (let i = 0; i < 30; i++) {
      setTimeout(() => createGift(), i * 100);
    }

    const fireworkInterval = setInterval(() => {
      launchFirework();
      setTimeout(() => launchFirework(), 200);
      setTimeout(() => launchFirework(), 400);
    }, 1200);

    const timer = setTimeout(() => {
      handlePreloaderFinish();
    }, 5000);

    const drawGift = (gift: Gift) => {
      ctx.save();
      ctx.translate(gift.x, gift.y);
      ctx.rotate(gift.rotation);
      
      ctx.fillStyle = gift.color;
      ctx.fillRect(-gift.size / 2, -gift.size / 2, gift.size, gift.size);
      
      ctx.fillStyle = `rgba(0,0,0,0.2)`;
      ctx.fillRect(-gift.size / 2 + 3, -gift.size / 2 + 3, gift.size, gift.size);
      
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(-gift.size / 8, -gift.size / 2, gift.size / 4, gift.size);
      ctx.fillRect(-gift.size / 2, -gift.size / 8, gift.size, gift.size / 4);
      
      ctx.fillStyle = "#ffd966";
      ctx.beginPath();
      ctx.ellipse(-gift.size / 3, -gift.size / 3, gift.size / 5, gift.size / 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(gift.size / 3, -gift.size / 3, gift.size / 5, gift.size / 4, 0, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = "#ffd966";
      ctx.fillRect(-gift.size / 8, -gift.size / 2, gift.size / 12, gift.size / 2);
      ctx.fillRect(gift.size / 12, -gift.size / 2, gift.size / 12, gift.size / 2);
      
      ctx.restore();
    };

    const animate = () => {
      const now = Date.now();
      let delta = Math.min(0.033, (now - lastTime) / 1000);
      lastTime = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#0f0c29');
      gradient.addColorStop(0.5, '#302b63');
      gradient.addColorStop(1, '#24243e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < 100; i++) {
        if (i % 2 === 0) continue;
        ctx.fillStyle = `rgba(255,255,200,${0.3 + Math.sin(Date.now() * 0.001 * (i % 10)) * 0.2})`;
        ctx.beginPath();
        ctx.arc((i * 131) % canvas.width, (i * 253) % canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      gifts.forEach((gift, index) => {
        gift.x += gift.vx * delta * 60;
        gift.y += gift.vy * delta * 60;
        gift.rotation += gift.rotationSpeed * delta * 60;
        
        drawGift(gift);
        
        if (gift.y > canvas.height + 100 || gift.x < -100 || gift.x > canvas.width + 100) {
          gifts.splice(index, 1);
          createGift();
        }
      });
      
      particles.forEach((p, index) => {
        p.x += p.vx * delta * 60;
        p.y += p.vy * delta * 60;
        p.vy += p.gravity * delta * 60;
        p.life -= 0.02 * delta * 60;
        p.alpha = p.life;
        
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        
        if (p.life <= 0 || p.y > canvas.height + 100) {
          particles.splice(index, 1);
        }
      });
      
      ctx.globalAlpha = 1;
      
      const time = Date.now() / 1000;
      const scale = 1 + Math.sin(time * 2) * 0.03;
      
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.scale(scale, scale);
      
      ctx.font = `bold ${Math.min(48, canvas.width / 10)}px "Playfair Display", "Segoe UI", cursive`;
      ctx.fillStyle = "#ffd966";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 15;
      ctx.textAlign = "center";
      ctx.fillText("Joyeux Anniversaire", 0, -40);
      
      ctx.font = `${Math.min(36, canvas.width / 12)}px "Space Grotesk", "Segoe UI", sans-serif`;
      ctx.fillStyle = "#ffffff";
      ctx.fillText(anniversaire?.personne || "", 0, 30);
      
      ctx.font = `${Math.min(32, canvas.width / 14)}px "Space Grotesk", "Segoe UI", sans-serif`;
      ctx.fillStyle = "#ffb347";
      ctx.fillText(`${anniversaire?.age || 0} ans`, 0, 90);
      
      ctx.restore();
      
      ctx.font = "14px 'Space Grotesk', sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.textAlign = "center";
      ctx.fillText("Preparez-vous a feter...", canvas.width / 2, canvas.height - 50);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      clearTimeout(timer);
      clearInterval(fireworkInterval);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [showPreloader, anniversaire]);

  if (loading) {
    return (
      <div className="invitation-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de votre invitation...</p>
      </div>
    );
  }

  if (error || !anniversaire) {
    return (
      <div className="invitation-error">
        <svg className="error-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <h2>Invitation introuvable</h2>
        <p>{error || "Cette invitation n'existe pas ou a expiree."}</p>
      </div>
    );
  }

  if (showPreloader) {
    return (
      <div className="anniversaire-preloader">
        <canvas ref={canvasRef} className="preloader-canvas" />
      </div>
    );
  }

  const imageUrl = anniversaire.image 
    ? `${API_URL}/uploads/anniversaires/${anniversaire.image}`
    : "";

  const isConfirmed = confirmationStatus === "confirmed";

  return (
    <div className="invitation-wrapper" style={{ backgroundColor: anniversaire.theme.background }}>
      <div className="hero-section">
        {imageUrl && (
          <img src={imageUrl} alt="Invitation Anniversaire" className="hero-image" />
        )}
      </div>
      
      <div className="content-section">
        <div className="content-container">
          <div className="qr-container">
            <div className="qr-frame" style={{ borderColor: anniversaire.theme.button }}>
              <div className="qr-wrapper" ref={qrRef}></div>
            </div>
          </div>
          
          {anniversaire.rsvp && (
            <div className="rsvp-container">
              <button
                className={`btn ${isConfirmed ? 'btn-decline' : 'btn-confirm'}`}
                style={isConfirmed ? {
                  backgroundColor: "transparent",
                  borderColor: anniversaire.theme.accent,
                  color: anniversaire.theme.text
                } : {
                  backgroundColor: anniversaire.theme.button,
                  color: anniversaire.theme.background
                }}
                onClick={handleResponse}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Chargement..."
                ) : isConfirmed ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Annuler
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 13l4 4L19 7"/>
                    </svg>
                    Confirmer
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnniversaireInvitation;